import {
  Box,
  Flex,
  Text,
  Stack,
  HStack,
  IconButton,
  useDisclosure,
  Spinner,
} from '@chakra-ui/react'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  DownIcon,
  RightIcon,
  SearchIcon,
} from '@/app/[locale]/_assets/icon-components'
import Search from './_components/Search'
import { useRouter } from '@/i18n/navigation'
import { useTranslations } from 'next-intl'
import Points from '../../../_components/Points'
import Auth from '@/app/[locale]/_components/Auth'
import DeliveryType from './_components/DeliveryType'
import { IOrderType, IRedux } from '@/app/[locale]/_store/types'
import { Skeleton, SkeletonText } from '@/components/ui/skeleton'
import { onOpenMap } from '@/app/[locale]/_store/common/common.slice'
import { createUrlWithParams } from '@/app/[locale]/_utils/createUrlWithParams'
import AutoGeolocate from '@/app/[locale]/_components/AutoGeolocate'
import Link from 'next/link'
import HeaderLogo from '@/app/[locale]/_components/HeaderLogo'
import { getHeaderLogoConfig } from '@/app/[locale]/_utils/getHeaderLogoConfig'

const HeaderMobile = () => {
  const [open, setOpen] = useState(false)
  const [isOpen, setOpenModal] = useState(false)

  const { open: isAuth, onClose } = useDisclosure()

  const router = useRouter()
  const t = useTranslations()
  const dispatch = useDispatch()

  const { settings, points, branch, is_hall, permissions, order_type } =
    useSelector(
      (state: IRedux) => ({
        settings: state.settings.data,
        points: state.common.points,
        branch: state.common.branch,
        is_hall: state.session.is_hall,
        permissions: state.session.permissions,
        order_type: state.session.order_type,
      }),
      shallowEqual
    )

  const logoConfig = useMemo(
    () => getHeaderLogoConfig(settings?.data),
    [settings?.data]
  )

  const activePoint = useMemo(
    () => points.find((point) => point?.isActive),
    [points]
  )

  const handleOpenMap = (type: IOrderType['type'] | null) => {
    if (type === 'delivery' && points?.length > 0) {
      return setOpenModal(true)
    }

    dispatch(onOpenMap())
  }

  const showAddressBeside =
    logoConfig.addressPlacement !== 'hidden' &&
    (logoConfig.addressPlacement === 'beside_logo' ||
      !logoConfig.addressPlacement) &&
    is_hall !== 'true'

  const addressMaxW = logoConfig.isCompactLayout ? '70vw' : '162px'

  const besideAddressRow = showAddressBeside ? (
    <Flex
      alignItems='center'
      gap='6px'
      fontSize='sm'
      color='text'
      onClick={() => handleOpenMap('delivery')}
    >
      <Text maxW={addressMaxW} truncate>
        {activePoint?.address && permissions.geolocation === 'accepted' ? (
          activePoint?.address
        ) : permissions.geolocation === 'pending' ? (
          <>
            {t('location.determining_location')}
            <Spinner size='xs' ml={1} />
          </>
        ) : (
          t('add_address')
        )}
      </Text>
      <RightIcon />
    </Flex>
  ) : null

  const brandBlock = logoConfig.isCompactLayout ? (
    <Stack gap={1} flex={1} minW={0}>
      <Link href='/'>
        <HeaderLogo config={logoConfig} variant='mobile' />
      </Link>
      {besideAddressRow}
    </Stack>
  ) : (
    <HStack gap={4} flex={1} minW={0}>
      <Link href='/'>
        <HeaderLogo config={logoConfig} variant='mobile' />
      </Link>
      <Flex flexDir='column' minW={0}>
        {logoConfig.showBrandName && (
          <Text fontWeight='bold'>{logoConfig.shipperName}</Text>
        )}
        {besideAddressRow}
      </Flex>
    </HStack>
  )

  return (
    <>
      <AutoGeolocate />
      <Box px={4} pt={4}>
        <HStack justifyContent='space-between'>
          {!settings?.shipper_id ? (
            <HStack gap={4} flex={1}>
              <Skeleton height='48px' width='48px' rounded={12} />
              <Stack gap={1} style={{ flex: 1 }}>
                <SkeletonText noOfLines={1} width='100px' />
                <SkeletonText noOfLines={1} width='150px' />
              </Stack>
            </HStack>
          ) : (
            brandBlock
          )}
          <HStack>
            <IconButton
              onClick={() => setOpen(true)}
              variant='ghost'
              rounded='full'
              color='text.muted'
            >
              <SearchIcon />
            </IconButton>
          </HStack>
        </HStack>
        {logoConfig.addressPlacement === 'header_bottom' && (
          <Stack mt={3}>
            {is_hall !== 'true' &&
              order_type?.type &&
              ['delivery', 'self-pickup'].includes(order_type?.type) && (
                <HStack justifyContent='space-between' alignItems='center'>
                  <Box
                    fontSize='sm'
                    onClick={() => handleOpenMap(order_type?.type)}
                  >
                    <Text color='text.muted' fontSize='xs'>
                      {order_type?.type === 'delivery'
                        ? t('delivery')
                        : t('from_the_branch')}
                    </Text>
                    <Flex alignItems='center' gap={1} flex={1}>
                      <Text maxW='45vw' truncate>
                        {order_type?.type === 'delivery' ? (
                          activePoint?.address &&
                          permissions.geolocation === 'accepted' ? (
                            activePoint?.address
                          ) : permissions.geolocation === 'pending' ? (
                            <>
                              {t('location.determining_location')}
                              <Spinner size='xs' ml={1} />
                            </>
                          ) : (
                            t('add_address')
                          )
                        ) : (
                          branch?.name || t('add_address')
                        )}
                      </Text>
                      {!(
                        order_type?.type === 'delivery' &&
                        permissions.geolocation === 'accepted'
                      ) && <DownIcon />}
                    </Flex>
                  </Box>
                  <DeliveryType handleOpenMap={handleOpenMap} />
                </HStack>
              )}
          </Stack>
        )}
      </Box>
      <Search open={open} setOpen={setOpen} />
      <Auth
        open={isAuth}
        onClose={onClose}
        onLastConfirm={() => router.push(createUrlWithParams('/profile'))}
      />
      <Points
        isOpen={isOpen && points?.length > 0}
        onClose={() => setOpenModal(false)}
      />
    </>
  )
}

export default HeaderMobile
